import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Box,
  Link,
  Card,
  TextField,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TableHead,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Grid,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SortableContainer, SortableHandle, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { styled } from '@mui/material/styles';
import { getDownloadURL, uploadBytesResumable, ref } from 'firebase/storage';
import Iconify from '../components/iconify';
import categoryService from '../services/category.service';
import productService from '../services/product.service';
import { loading } from '../reducers/general.reducer';
import { currencyFormatter } from '../utils/formatNumber';

import { storage } from '../config/firebase';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DragHandle = SortableHandle(({ style }) => (
  <span style={{ ...style, ...{ cursor: 'move' } }}>
    <Iconify icon="mdi:drag-horizontal-variant" />
  </span>
));

const TableBodySortable = SortableContainer(({ children }) => <TableBody>{children}</TableBody>);

TableBodySortable.muiName = 'TableBody';

const Row = SortableElement(({ data, handleOpenDelete, handleOpenAddEdit, onChangeActive, ...other }) => (
  <TableRow {...other}>
    <TableCell style={{ width: '5%' }}>
      <DragHandle />
    </TableCell>
    <TableCell style={{ width: '45%' }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            backgroundImage:
              // eslint-disable-next-line global-require
              `url(${data && data.photo ? data.photo : '/assets/images/placeholder-image.png'})`,
            backgroundRepeat: 'no-repeat',
            height: 50,
            width: 50,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            border: '1px solid #ECECEC',
            borderRadius: 1,
            cursor: 'pointer',
          }}
          onClick={() => handleOpenAddEdit && handleOpenAddEdit(data)}
        />
        <Typography variant="subtitle2" noWrap>
          {data.name}
        </Typography>
      </Stack>
    </TableCell>
    <TableCell>
      <Typography variant="body1" noWrap>
        {data.price && currencyFormatter(data.price, undefined)}
      </Typography>
    </TableCell>
    <TableCell align="right">
      <Switch checked={data.active} color="success" onChange={() => onChangeActive && onChangeActive(data.id)} />
      <IconButton size="large" color="inherit" onClick={() => handleOpenAddEdit && handleOpenAddEdit(data)}>
        <Iconify icon={'eva:edit-outline'} />
      </IconButton>
      <IconButton size="large" color="error" onClick={() => handleOpenDelete && handleOpenDelete(data)}>
        <Iconify icon={'eva:trash-2-outline'} />
      </IconButton>
    </TableCell>
  </TableRow>
));

export default function Page() {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [openDelete, setOpenDelete] = useState(false);
  const [openAddEdit, setOpenAddEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(undefined);
  const [selectedFile, setSelectedFile] = useState(undefined);
  const [data, setData] = useState([]);

  const fetchData = useCallback(async () => {
    dispatch(loading(true));
    const _categories = await categoryService.getAll(user.account, 1);
    const _products = await productService.getAll(user.account, 1);
    const _data = _categories.map((category) => ({
      ...category,
      products: _products.length > 0 ? _products.filter((it) => it.categoryId === category.id) : [],
    }));
    setData(_data);
    dispatch(loading(false));
  }, [dispatch, user.account]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      categoryId: '',
      name: '',
      description: '',
      price: 0,
      submit: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().max(255).required('O NOME é obrigatório'),
      price: Yup.number().moreThan(0, 'O PREÇO é inválido'),
    }),
    onSubmit: async (values, helpers) => {
      try {
        dispatch(loading(true));
        if (selectedRow && selectedRow.id) {
          await productService.edit(user.account, 1, selectedRow.id, {
            ...selectedRow,
            name: values.name,
            description: values.description,
            price: values.price,
          });
        } else {
          await productService.add(user.account, 1, selectedRow.categoryId, {
            ...selectedRow,
            name: values.name,
            description: values.description,
            price: values.price,
          });
        }
        if (selectedFile && selectedFile[0]) {
          uploadFile(selectedFile[0]);
        }
        await fetchData();
        setOpenAddEdit(false);
        setSelectedFile(undefined);
        setSelectedRow(undefined);
      } catch (error) {
        let message = 'Ops! Algo de errado aconteceu';
        if (error.response && error.response.data && error.response.data.message[0]) {
          message = error.response.data.message.join(', ');
        }
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: message });
        helpers.setSubmitting(false);
      } finally {
        dispatch(loading(false));
      }
    },
  });

  const handleOpenDelete = (row) => {
    setSelectedRow(row);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setSelectedRow(undefined);
    setOpenDelete(false);
  };

  const handleDelete = async () => {
    if (selectedRow && selectedRow.id) {
      dispatch(loading(true));
      await productService.remove(user.account, 1, selectedRow.id);
      const categoryIndex = data.map((e) => e.id).indexOf(selectedRow.categoryId);
      const _products = [...data[categoryIndex].products];
      const index = _products.map((e) => e.id).indexOf(selectedRow.id);
      _products.splice(index, 1);
      const _data = [...data];
      _data[categoryIndex] = {
        ..._data[categoryIndex],
        products: _products,
      };
      setData(_data);
      dispatch(loading(false));
    }
    handleCloseDelete();
  };

  const handleOpenAddEdit = (categoryId, row) => {
    formik.setFieldValue('name', row ? row.name : '');
    formik.setFieldValue('description', row ? row.description : '');
    formik.setFieldValue('price', row ? row.price : 0);
    setSelectedRow({ active: false, ...row, categoryId });
    setOpenAddEdit(true);
  };

  const handleCloseAddEdit = () => {
    setSelectedFile(undefined);
    setSelectedRow(undefined);
    setOpenAddEdit(false);
  };

  const onChangeActive = async (categoryId, id) => {
    const _data = [...data];
    const categoryIndex = _data.map((e) => e.id).indexOf(categoryId);
    const _products = [..._data[categoryIndex].products];
    const index = _products.map((e) => e.id).indexOf(id);
    _products[index] = {
      ..._products[index],
      active: !_products[index].active,
    };
    _data[categoryIndex] = {
      ..._data[categoryIndex],
      products: _products,
    };
    setData(_data);
    await productService.edit(user.account, 1, _products[index].id, { ..._products[index] });
  };

  const onSortEnd = async ({ categoryId, oldIndex, newIndex }) => {
    dispatch(loading(true));
    const categoryIndex = data.map((e) => e.id).indexOf(categoryId);
    let _productsAux = data[categoryIndex].products.filter((it) => it.categoryId === categoryId);
    _productsAux = arrayMoveImmutable(_productsAux, oldIndex, newIndex).map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    const _data = [...data];
    _data[categoryIndex] = {
      ..._data[categoryIndex],
      products: _productsAux,
    };
    setData(_data);
    await _productsAux.map(async (item) => productService.edit(user.account, 1, item.id, item));
    dispatch(loading(false));
  };

  const uploadFile = (file) => {
    const storageRef = ref(storage, file.name);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`upload is${progress}% done`);
        switch (snapshot.state) {
          case 'paused':
            console.log('Upload paused');
            break;
          case 'running':
            console.log('Upload running');
            break;
          default:
            break;
        }
      },
      (error) => {
        console.log(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadedURL) => {
          console.log(downloadedURL);
          // you keep uploaded img url
          // setData((prev) => ({ ...prev, img: downloadedURL }));
        });
      }
    );
  };

  return (
    <>
      <Helmet>
        <title> Produtos | BMENU </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" gutterBottom>
            PRODUTOS
          </Typography>
        </Stack>
        <Breadcrumbs aria-label="breadcrumb" mb={5}>
          <Link underline="hover" color="inherit" href="/dashboard/home">
            Dashboard
          </Link>
          <Typography color="text.primary">Menu</Typography>
          <Typography color="text.primary">Produtos</Typography>
        </Breadcrumbs>

        <Grid container spacing={2}>
          {data.map((row, index) => {
            const category = row;
            return (
              <Grid key={index} item xs={12}>
                <Card>
                  <Accordion>
                    <Accordion defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel3-content"
                        id="panel3-header"
                      >
                        {category.name}{' '}
                        <Iconify
                          icon="mdi:circle-medium"
                          style={category.active ? { color: '#54D62C' } : { color: '#FF4842' }}
                        />
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell />
                                <TableCell>PRODUTO</TableCell>
                                <TableCell>PREÇO</TableCell>
                                <TableCell>
                                  <Stack direction="row" justifyContent="end">
                                    <Button variant="text" size="small" onClick={() => handleOpenAddEdit(category.id)}>
                                      <Iconify icon="mdi:add" /> ADICIONAR
                                    </Button>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            {category.products.length > 0 && (
                              <TableBodySortable
                                onSortEnd={(e) => onSortEnd({ ...e, categoryId: category.id })}
                                useDragHandle
                              >
                                {category.products.map((product, index) => (
                                  <Row
                                    index={index}
                                    key={index}
                                    data={product}
                                    handleOpenDelete={() => handleOpenDelete(product)}
                                    handleOpenAddEdit={() => handleOpenAddEdit(category.id, product)}
                                    onChangeActive={() => onChangeActive(category.id, product.id)}
                                  />
                                ))}
                              </TableBodySortable>
                            )}
                            {category.products.length === 0 && (
                              <TableBody>
                                <TableRow>
                                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                                    <Paper
                                      sx={{
                                        textAlign: 'center',
                                      }}
                                    >
                                      <Typography variant="body2">
                                        Nenhum produto cadastrado para &nbsp;
                                        <strong>&quot;{category.name}&quot;</strong>.
                                      </Typography>
                                    </Paper>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            )}
                            {/* <TableBodySortable onSortEnd={onSortEnd} useDragHandle>
                            {data.map((row, index) => (
                              <Row
                                index={index}
                                key={index}
                                data={row}
                                handleOpenDelete={() => handleOpenDelete(row)}
                                handleOpenAddEdit={() => handleOpenAddEdit(row)}
                                onChangeActive={() => onChangeActive(row.id)}
                              />
                            ))}
                          </TableBodySortable> */}
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </Accordion>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{'Tem certeza de que quer excluir este produto?'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{selectedRow ? selectedRow.name : ''}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>CANCELAR</Button>
          <Button onClick={handleDelete} autoFocus sx={{ color: 'error.main' }}>
            CONFIRMAR
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openAddEdit}
        onClose={handleCloseAddEdit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <form noValidate onSubmit={formik.handleSubmit}>
          <DialogTitle id="alert-dialog-title">{selectedRow && selectedRow.id ? 'EDITAR' : 'ADICIONAR'}</DialogTitle>
          <DialogContent>
            <Box sx={{ paddingTop: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        error={!!(formik.touched.name && formik.errors.name)}
                        fullWidth
                        helperText={formik.touched.name && formik.errors.name}
                        label="NOME"
                        name="name"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.name}
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        error={!!(formik.touched.description && formik.errors.description)}
                        fullWidth
                        helperText={formik.touched.description && formik.errors.description}
                        label="DESCRIÇÃO"
                        name="description"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.description}
                        autoComplete="off"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        error={!!(formik.touched.price && formik.errors.price)}
                        fullWidth
                        helperText={formik.touched.price && formik.errors.price}
                        label="PREÇO"
                        name="price"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.price}
                        type="number"
                      />
                    </Grid>

                    {formik.errors.submit && (
                      <Grid item xs={12} textAlign={'center'}>
                        <Typography color="error" variant="body2">
                          {formik.errors.submit} {formik.values.active}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} textAlign={'right'}>
                      <Switch
                        checked={selectedRow ? selectedRow.active : false}
                        color="success"
                        onChange={() => setSelectedRow({ ...selectedRow, active: !selectedRow.active })}
                        name="active"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                {/* <Grid item xs={12} md={4} container justify="center" justifyContent={'center'}>
                  <Box
                    sx={{
                      backgroundImage: `url(${
                        selectedFile && selectedFile[0]
                          ? URL.createObjectURL(selectedFile[0])
                          : '/assets/images/placeholder-image.png'
                      })`,
                      backgroundRepeat: 'no-repeat',
                      height: 200,
                      width: '100%',
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      mb: 1,
                      border: '1px solid #ECECEC',
                      borderRadius: 2,
                    }}
                  />
                  <Button component="label" role={undefined} tabIndex={-1} startIcon={<CloudUploadIcon />}>
                    ENVIAR FOTO
                    <VisuallyHiddenInput type="file" onChange={(event) => setSelectedFile(event.target.files)} />
                  </Button>
                </Grid> */}
                {/* <Grid
                  item
                  xs={12}
                  md={6}
                  container
                  justify="center"
                  alignItems="center"
                  justifyContent={'center'}
                  spacing={2}
                >
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                  >
                    ENVIAR FOTO
                    <VisuallyHiddenInput type="file" onChange={(event) => console.log(event.target.files)} />
                  </Button>
                </Grid> */}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddEdit}>CANCELAR</Button>
            <Button type="submit" autoFocus sx={{ color: 'error.main' }}>
              CONFIRMAR
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}
